// Emergency Department — shared vocabulary and display tokens.
//
// Triage colours are the one place this module deliberately steps outside the
// IPD navy/teal palette: Red / Yellow / Green / Black are clinical signals a
// nurse reads across the room, not brand decoration, so they keep their
// conventional meaning. Everything else on screen stays on the IPD tokens.

import { IPD_COLORS } from "../ipd/utils";

/**
 * Makes a select ellipsize its chosen option instead of widening to fit it.
 *
 * Doctor options carry the full specialisation list — "Karmakar — Stroke
 * Management, Epilepsy, Headache & Migraine" — which is wider than the narrow
 * dialogs that offer them. Without this the field grows, the dialog scrolls
 * sideways and the field labels are clipped out of view. Pair it with a
 * shrinkable container (`gridTemplateColumns: "minmax(0, 1fr)"` and
 * `minWidth: 0`), since a grid item will not shrink below its content on its
 * own. The dropdown still shows the whole label.
 */
export const truncatedSelectSx = {
  "& .MuiSelect-select": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

export const TRIAGE = {
  red: {
    label: "Red",
    description: "Life-threatening — seen immediately",
    color: "#C62828",
    bg: "#FDECEA",
    border: "#EF9A9A",
    text: "#B91C1C",
  },
  yellow: {
    label: "Yellow",
    description: "Urgent but not life-threatening",
    color: "#F9A825",
    bg: "#FFF8E1",
    border: "#FFE082",
    text: "#B45309",
  },
  green: {
    label: "Green",
    description: "Minor injury or illness",
    color: "#2E7D32",
    bg: "#E6F4EA",
    border: "#A5D6A7",
    text: "#1E7E34",
  },
  black: {
    label: "Black",
    description: "Deceased",
    color: "#37474F",
    bg: "#ECEFF1",
    border: "#B0BEC5",
    text: "#263238",
  },
};

/** Most severe first. Untriaged patients sort last — see TRIAGE_RANK_UNTRIAGED. */
export const TRIAGE_ORDER = ["red", "yellow", "green", "black"];
export const TRIAGE_RANK_UNTRIAGED = TRIAGE_ORDER.length;

export const triageRank = (category) => {
  const i = TRIAGE_ORDER.indexOf(category);
  return i === -1 ? TRIAGE_RANK_UNTRIAGED : i;
};

/**
 * Where a patient has got to in the visit. Derived from the visit rather than
 * stored, so it can never drift out of step with the fields it describes.
 */
export const STAGE = {
  awaitingTriage: { key: "awaitingTriage", label: "Awaiting triage", ...IPD_COLORS.warning },
  awaitingBed: { key: "awaitingBed", label: "Awaiting bed", ...IPD_COLORS.info },
  inTreatment: { key: "inTreatment", label: "In treatment", ...IPD_COLORS.success },
  observation: {
    key: "observation",
    label: "Under observation",
    bg: "#EDE9FE",
    text: "#5B21B6",
    border: "#C4B5FD",
  },
  closed: {
    key: "closed",
    label: "Closed",
    bg: "#F1F5F9",
    text: IPD_COLORS.textSecondary,
    border: IPD_COLORS.border,
  },
};

/**
 * Derived rather than stored, so it can never drift out of step with the
 * fields it describes.
 *
 * Order matters: observation outranks having a bed, since it says more about
 * where the patient actually is.
 */
export function visitStage(visit) {
  if (!visit) return STAGE.awaitingTriage;
  if (visit.status === "closed" || visit.disposition) return STAGE.closed;
  if (visit.observation_started_at) return STAGE.observation;
  if (visit.bed_id) return STAGE.inTreatment;
  if (visit.triage_category) return STAGE.awaitingBed;
  return STAGE.awaitingTriage;
}

export const ARRIVAL_MODE_LABEL = {
  "walk-in": "Walk-in",
  ambulance: "Ambulance",
};

/** The board refreshes itself on this cadence (ER-BRD-004/005) — periodic
 *  polling, deliberately not a live-push connection. */
export const BOARD_REFRESH_MS = 30000;

/** How long the patient has been in the department, as "1h 20m" / "8m". */
export function waitingSince(iso) {
  if (!iso) return "—";
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
